import { FC } from 'react';

type Option = string | { answer: string; bgColor?: string };

interface ChecklistMultiSelectProps {
  label: string;
  description?: string | null;
  isRequired?: boolean;
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  helperText?: string | null;
  onClear?: () => void;
}

const ChecklistMultiSelect: FC<ChecklistMultiSelectProps> = ({
  label,
  description,
  isRequired,
  options,
  value,
  onChange,
  disabled,
  helperText,
  onClear,
}) => {
  const getLabel = (opt: Option) => (typeof opt === 'string' ? opt : opt.answer);
  const getColor = (opt: Option) => (typeof opt === 'string' ? undefined : opt.bgColor);

  const handleToggle = (option: Option) => {
    if (disabled) {
      return;
    }

    const label = getLabel(option);
    const exists = value.includes(label);
    const next = exists ? value.filter((item) => item !== label) : [...value, label];
    onChange(next);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-800">
            {label}
            {isRequired ? <span className="ml-1 text-danger">*</span> : null}
          </p>
          {description ? <p className="mt-1 text-xs text-slate-500">{description}</p> : null}
        </div>
        {value.length > 0 && onClear ? (
          <button
            type="button"
            className="text-[11px] font-medium text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline disabled:opacity-50"
            onClick={onClear}
            disabled={disabled}
          >
            Clear
          </button>
        ) : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {options.length === 0 ? (
          <span className="text-xs text-slate-400">No options configured.</span>
        ) : (
          options.map((option) => {
            const label = getLabel(option);
            const isSelected = value.includes(label);
            const bg = getColor(option);
            return (
              <button
                key={label}
                type="button"
                className={`inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  isSelected
                    ? 'bg-primary text-white shadow-sm shadow-primary/20'
                    : 'border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary'
                } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                onClick={() => handleToggle(option)}
                disabled={disabled}
              >
                <span className="mr-2 inline-block h-3 w-3 rounded" style={{ backgroundColor: bg || '#fff', outline: bg ? '1px solid #e5e7eb' : '1px solid transparent' }} />
                {label}
              </button>
            );
          })
        )}
      </div>
      {helperText ? (
        <p className="mt-2 text-xs text-danger">{helperText}</p>
      ) : null}
    </div>
  );
};

export default ChecklistMultiSelect;
